// @flow

import { datadogRum } from '@datadog/browser-rum';
import { retrieve } from 'putki';
import { consentGivenForCategory } from 'utils/cookieConsent';

const environment = window.__ENVIRONMENT__;

// Regex patterns to identify known bot instances
// Source: https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/
const botPattern =
  '(googlebot\\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';

const botRegex = new RegExp(botPattern, 'i');

function isBotUserAgent() {
  return botRegex.test(navigator.userAgent);
}

let initialized = false;
function initializeDatadogRum() {
  if (initialized) return;
  initialized = true;

  // sessionSampleRate:
  //   The percentage of sessions to track: 100 for all, 0 for none. Only
  //   tracked sessions send RUM events. For more details about
  //   sessionSampleRate, see the sampling configuration.
  //    -> https://docs.datadoghq.com/real_user_monitoring/guide/sampling-browser-plans/
  //
  // sessionReplaySampleRate:
  //   The percentage of tracked sessions with Browser RUM & Session Replay
  //   pricing features: 100 for all, 0 for none. For more details about
  //   sessionReplaySampleRate, see the sampling configuration.
  //    -> https://www.datadoghq.com/pricing/?product=real-user-monitoring--session-replay#real-user-monitoring--session-replay
  //    -> https://docs.datadoghq.com/real_user_monitoring/guide/sampling-browser-plans/

  const { sessionSampleRate, sessionReplaySampleRate } = (() => {
    // Filter out bots from RUM monitoring
    // See: https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/
    if (isBotUserAgent()) {
      return { sessionSampleRate: 0, sessionReplaySampleRate: 0 };
    }

    switch (environment) {
      case 'test':
        // We don't want test environment to send any RUM events but we do
        // want our app to crash in test if for some reason the Datadog RUM
        // code messes up our application.
        return { sessionSampleRate: 0, sessionReplaySampleRate: 0 };
      case 'development':
        // We don't want to incur pricing costs in development.
        // This number can be changed when we want to test the RUM code.
        return { sessionSampleRate: 0, sessionReplaySampleRate: 0 };
      default:
        // We want to track all user sessions and let Datadog retention filters
        // handle the actual retention (keeping only ~1K out of ~260K sessions).
        // This allows us to capture all sessions for investigation while only
        // retaining error cases and requests that take a long time.
        //
        // We are not using session replays, though, so we disable that.
        return { sessionSampleRate: 100, sessionReplaySampleRate: 0 };
    }
  })();

  const controller = retrieve('controller');
  const action = retrieve('action');
  datadogRum.init({
    applicationId: '1b89e864-e532-4578-a391-cf29b0edb549',
    clientToken: 'pubf765aeae3809f6105bb0c168ce3687bd',
    site: 'datadoghq.eu',
    service: 'venuu',
    env: environment,
    version: retrieve('application_version_git'),
    sessionSampleRate,
    sessionReplaySampleRate,
    allowedTracingUrls: [/^https:\/\/(?:dev\.|staging\.)?venuu\.(?:fi|se|com)/],
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    beforeSend: event => {
      if (typeof controller === 'string' && typeof action === 'string') {
        // Datadog shows this in RUM session explorer for view names:
        //
        // > RUM SDK supports custom view names, which can be used to override
        // > `url_path_group`. If not specified, it will have the same content
        // > of `view.url_path_group`.
        //
        // So in order for venue pages, search pages etc. to be monitorable as
        // their own groups, we can set up a custom view name that uses the
        // Rails controller name and actions.
        //
        // This way, regardless of how pretty our URLs are, the grouping will
        // match the way we write our pages. And we can finally monitor how long
        // venue profile pages take to load.
        event.view.name = `${controller}#${action}`;
      }
    },
    defaultPrivacyLevel: 'mask-user-input'
  });

  datadogRum.startSessionReplayRecording();
}

document.addEventListener('cookieyes_consent_update', eventData => {
  // $FlowIgnore[prop-missing] -- https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
  const data = eventData.detail;
  if (data.accepted.includes('performance')) {
    initializeDatadogRum();
  }
});

if (consentGivenForCategory('performance') || environment === 'test') {
  initializeDatadogRum();
}
